import { useState, useEffect } from 'react'
import { addDays } from 'date-fns'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { theme } from '../../theme/theme'
import { Spinner } from '../icons/Spinner'
import { TimeSlotGrid } from './TimeSlotGrid'
import { useGetWeekTimeSlotsQuery } from '../../api/ester-b2b-api/private/timeSlots/timeSlotQueries'
import { transformWeekTimeSlotsResponse } from '../../utils/timeSlotCalendar'
import type { TimeSlotCalendarProps, TimeSlot, DayTimeSlots } from './types'
import { CalendarHeader } from './CalendarHeader'
import { getWeekStart, formatDateForAPI, getDaysInWeek, getPreviousWeek, getNextWeek } from '../../utils/dates'

export const TimeSlotCalendar = ({ onSlotSelect, selectedSlot, initialWeekStart }: TimeSlotCalendarProps) => {
  const { t } = useTranslation()
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
    initialWeekStart ? getWeekStart(initialWeekStart) : getWeekStart(new Date())
  )

  const [days, setDays] = useState<DayTimeSlots[]>([])

  const fromDate = formatDateForAPI(currentWeekStart)
  const toDate = formatDateForAPI(addDays(currentWeekStart, 6))

  const { data, isLoading, isError, error } = useGetWeekTimeSlotsQuery({
    fromDate,
    toDate,
  })

  // Transform API data to component format
  useEffect(() => {
    if (data) {
      const transformedDays = transformWeekTimeSlotsResponse(data)
      setDays(transformedDays)
    } else {
      // If no data, show empty slots
      const weekDays = getDaysInWeek(currentWeekStart)
      setDays(
        weekDays.map((date) => ({
          date,
          slots: [],
          isFullyBooked: false,
        }))
      )
    }
  }, [data, currentWeekStart])

  const handlePreviousWeek = () => {
    setCurrentWeekStart((prev) => getPreviousWeek(prev))
  }

  const handleNextWeek = () => {
    setCurrentWeekStart((prev) => getNextWeek(prev))
  }

  const handleSlotSelect = (slot: TimeSlot, date: Date) => {
    onSlotSelect(slot, date)
  }

  return (
    <Container>
      <CalendarHeader weekStart={currentWeekStart} onPreviousWeek={handlePreviousWeek} onNextWeek={handleNextWeek} />

      {isLoading && (
        <LoadingContainer>
          <Spinner size={40} />
          <LoadingText>{t('employee.healthCall.loading')}</LoadingText>
        </LoadingContainer>
      )}

      {isError && (
        <ErrorContainer>
          <ErrorText>{t('employee.healthCall.error')}</ErrorText>
          <ErrorMessage>{error?.message || t('employee.healthCall.errorMessage')}</ErrorMessage>
        </ErrorContainer>
      )}

      {!isLoading && !isError && (
        <TimeSlotGrid days={days} onSlotSelect={handleSlotSelect} selectedSlot={selectedSlot} />
      )}
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  padding: ${theme.spacing.mediumLarge}px;
  background-color: ${theme.color.white};
  border-radius: ${theme.spacing.small}px;
  height: 454px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 1));
    pointer-events: none;
    border-radius: 0 0 ${theme.spacing.small}px ${theme.spacing.small}px;
  }

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    padding: ${theme.spacing.medium}px;
    height: 366px;
  }

  @media screen and (max-width: ${theme.breakpoint.small}px) {
    border-radius: 0px;
    padding: ${theme.spacing.xsmall}px;
    height: 352px;

    &::after {
      border-radius: 0;
    }
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xxlarge}px;
  gap: ${theme.spacing.medium}px;
`

const LoadingText = styled.p`
  color: ${theme.color.grey3};
  font-size: ${theme.fontSize.small}px;
  margin: 0;
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xxlarge}px;
  gap: ${theme.spacing.small}px;
  background-color: ${theme.color.red}10;
  border-radius: ${theme.spacing.small}px;
`

const ErrorText = styled.h3`
  color: ${theme.color.red};
  font-size: ${theme.fontSize.medium}px;
  font-weight: 500;
  margin: 0;
`

const ErrorMessage = styled.p`
  color: ${theme.color.red};
  font-size: ${theme.fontSize.small}px;
  margin: 0;
  text-align: center;
`
